import type { LessonStartFailureKind } from "@/features/tutor/vo-demo/runtime/useLessonStartFailure";

interface LessonStartFailureScreenProps {
  kind: LessonStartFailureKind;
  isRetrying: boolean;
  onRetry: () => void;
}

const TITLE_BY_KIND: Record<LessonStartFailureKind, string> = {
  discovery: "We couldn't load your lesson",
  boot: "We couldn't start your lesson",
};

/**
 * Covers the player while a lesson start has failed. There is no session
 * behind it yet, so every control on the player would be a dead button. Stays
 * up while a retry runs, for the same reason.
 */
const LessonStartFailureScreen = ({
  kind,
  isRetrying,
  onRetry,
}: LessonStartFailureScreenProps) => (
  <div
    role="alert"
    aria-busy={isRetrying}
    className="fixed inset-0 z-[1000] flex items-center justify-center bg-white px-4"
  >
    <div className="flex max-w-sm flex-col items-center gap-3 text-center">
      <h2 className="font-mulish text-xl font-semibold text-[#252525]">
        {TITLE_BY_KIND[kind]}
      </h2>
      <p className="text-sm leading-6 text-[#3E4C53]">
        Check your connection. We'll try again as soon as you're back online.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying}
        className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#1C9C6D] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#178A5F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C9C6D]/40 disabled:cursor-wait disabled:opacity-70 disabled:hover:bg-[#1C9C6D]"
      >
        {isRetrying && (
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
          />
        )}
        {isRetrying ? "Trying again..." : "Try again"}
      </button>
    </div>
  </div>
);

export default LessonStartFailureScreen;
